<script lang="ts" setup>
  import type { PropType } from 'vue'
  import {
    EntitiesMapperKeyEnum,
    type TDataMapper,
    type ProposedEntityChange,
    ProposedEntityChangeStatusEnum
  } from '@/types/generated'
  import { FieldTypeEnum } from '@/types/generated'
  import { computed, ref, watch } from 'vue'
  import { yup } from '@/lib/validation'
  import ComboField from '@/components/admin/form/ComboField.vue'
  import InputField from '@/components/admin/form/InputField.vue'
  import SelectField from '@/components/admin/form/SelectField.vue'
  import CheckboxField from '@/components/admin/form/CheckboxField.vue'
  import TextAreaField from '@/components/admin/form/TextAreaField.vue'
  import { CheckIcon, CopyIcon, X } from 'lucide-vue-next'
  import { Button } from '@/components/ui/button'
  import { useTranslation } from '@/composables/useTranslation'
  import { useMetrics, MetricEventType } from '@/composables/useMetrics'
  import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'
  import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip'

  const props = defineProps({
    value: {
      // Using type: null tells Vue to accept any type for this prop
      // This is the Vue-specific way to allow any value type
      type: null,
      required: true
    },
    mapper: {
      type: Object as PropType<TDataMapper>,
      required: true
    },
    disabled: {
      type: Boolean,
      required: false,
      default: false
    },
    customValidator: {
      type: Object as PropType<yup.AnySchema | null>,
      required: false,
      default: () => null
    },
    proposedChange: {
      type: Object as PropType<ProposedEntityChange | null>,
      required: false,
      default: () => null
    },
    /** Wenn gesetzt, wird beim Kopieren ein PostHog-Event getrackt (z. B. "law_specific_data" oder "entities"). */
    copyTrackSource: {
      type: String as PropType<'law_specific_data' | 'entities'>,
      required: false,
      default: undefined
    }
  })

  const proposedValue = computed<string | null>(() => {
    if (!props.proposedChange?.proposed_value) return null

    const rawValue = props.proposedChange.proposed_value as { value: any }
    return rawValue.value
  })

  const emit = defineEmits([
    'onValueChange',
    'valueChangeError',
    'acceptProposedChange',
    'rejectProposedChange'
  ])

  const { t } = useTranslation({
    en: {
      showMore: 'Show more',
      fullText: 'Full Text',
      accept: 'Accept',
      reject: 'Reject',
      copy: 'Copy',
      copied: 'Copied!'
    },
    de: {
      showMore: 'Mehr anzeigen',
      fullText: 'Vollständiger Text',
      accept: 'Bestätigen',
      reject: 'Ablehnen',
      copy: 'Kopieren',
      copied: 'Kopiert!'
    }
  })

  const { trackEvent } = useMetrics()

  const localValue = ref(props.value)

  const rawValue = ref(props.value)

  const errorMessage = ref('')

  // Update local value when prop changes
  watch(
    () => props.value,
    (newValue) => {
      localValue.value = newValue
    }
  )

  const isPopoverOpen = ref(false)
  const isMultiSelectOpen = ref(false)
  // Computed property to check if the text is long enough to show the "Show more" button
  const isLongText = computed(() => {
    const currentValue = modelValue.value || props.value
    return typeof currentValue === 'string' && currentValue.length > 150
  })

  const validationRules = computed(() => {
    if (!props.mapper.field_type) return null

    const schema = (() => {
      if (props.customValidator) {
        return props.customValidator
      }

      switch (props.mapper.field_type) {
        case FieldTypeEnum.NUMBERINPUT:
          return yup.number().integer().nullable()
        case FieldTypeEnum.DECIMALNUMBERINPUT:
          return yup.number().nullable()
        case FieldTypeEnum.TEXTINPUT:
        case FieldTypeEnum.TEXTAREA:
          return yup.string().nullable()
        case FieldTypeEnum.EMAILINPUT:
          return yup.string().email().nullable()
        case FieldTypeEnum.PHONEINPUT:
          return yup.string().phone().nullable()
        case FieldTypeEnum.URLINPUT:
          return yup.string().url().nullable()
        case FieldTypeEnum.SELECT:
          return yup
            .string()
            .oneOf(props.mapper.choices || [], 'Please select a valid option')
            .nullable()
        case FieldTypeEnum.DATEFIELD:
          return yup
            .string()
            .nullable()
            .matches(
              /^(0[1-9]|[12][0-9]|3[01])\.(0[1-9]|1[0-2])\.(19|20)\d{2}$/,
              'Bitte geben Sie ein gültiges Datum im Format TT.MM.JJJJ ein'
            )
            .test('is-valid-date', 'Bitte geben Sie ein existierendes Datum ein', (value) => {
              if (!value) return true // allow empty if nullable
              const [day, month, year] = value.split('.').map(Number)
              const date = new Date(year, month - 1, day)
              return (
                date.getFullYear() === year &&
                date.getMonth() === month - 1 &&
                date.getDate() === day
              )
            })
        case FieldTypeEnum.TIMEFIELD:
          return yup
            .string()
            .nullable()
            .matches(
              /^([01]?[0-9]|2[0-3]):[0-5][0-9]$/,
              'Bitte geben Sie eine gültige Uhrzeit im Format HH:MM ein'
            )
        case FieldTypeEnum.YESNO:
          return props.mapper.is_nullable ? yup.boolean().nullable() : yup.boolean()
        default:
          return null
      }
    })()

    return schema
  })

  const validateValue = (value: any) => {
    if (!validationRules.value) return
    validationRules.value
      .validate(value)
      .then(() => {
        emit('valueChangeError', props.mapper.key, false)
        errorMessage.value = ''
      })
      .catch((error: Error) => {
        emit('valueChangeError', props.mapper.key, true)
        errorMessage.value = error.message
      })
  }

  // Watch for changes in validation rules and re-validate current value
  watch(
    validationRules,
    () => {
      validateValue(localValue.value)
    },
    { deep: true }
  )

  const modelValue = computed({
    get: () => {
      // When getting values, ensure numbers are properly formatted for display
      if (
        props.mapper.field_type === FieldTypeEnum.NUMBERINPUT ||
        props.mapper.field_type === FieldTypeEnum.DECIMALNUMBERINPUT
      ) {
        // If the value is a string that represents a number, parse it for proper display
        if (typeof localValue.value === 'string' && !isNaN(Number(localValue.value))) {
          return Number(localValue.value)
        }
      }

      // validate raw value
      validateValue(rawValue.value)

      return localValue.value
    },
    set: async (newValue) => {
      // always save the raw value to verify it with the local value
      rawValue.value = newValue

      // start processing the new value
      let normalizedNewValue = newValue

      // Normalize numeric values to ensure consistent type handling
      if (
        props.mapper.field_type === FieldTypeEnum.NUMBERINPUT ||
        props.mapper.field_type === FieldTypeEnum.DECIMALNUMBERINPUT
      ) {
        // If the value is a number, convert it to string to ensure consistent storage
        if (typeof normalizedNewValue === 'number') {
          normalizedNewValue = String(normalizedNewValue)
        } else if (typeof normalizedNewValue === 'string' && normalizedNewValue !== '') {
          // Ensure string numbers are properly formatted (no extra spaces, etc.)
          const parsedNum = Number(normalizedNewValue)
          if (!isNaN(parsedNum)) {
            normalizedNewValue = String(parsedNum)
          }
        }
      }

      if (localValue.value === normalizedNewValue) {
        return
      }

      if (!validationRules.value) {
        localValue.value = normalizedNewValue
        emit('onValueChange', normalizedNewValue)
        emit('valueChangeError', props.mapper.key, false)
        errorMessage.value = ''
        return
      }

      try {
        await validationRules.value.validate(normalizedNewValue)
        localValue.value = normalizedNewValue
        emit('onValueChange', normalizedNewValue)
        emit('valueChangeError', props.mapper.key, false)
        errorMessage.value = ''
      } catch (error) {
        // Value is invalid, don't emit the change
        // but, emit error presence instead
        emit('valueChangeError', props.mapper.key, true)
        errorMessage.value = (error as Error).message
      }
    }
  })

  // Predefined options for specific title combo field
  const specificTitleOptions = [
    { value: 'Prof.', title: 'Prof.' },
    { value: 'Prof. Dr.', title: 'Prof. Dr.' },
    { value: 'Dr.', title: 'Dr.' },
    { value: 'Dr. Dr.', title: 'Dr. Dr.' }
  ]

  const renderComboField = computed<Boolean>(() => {
    // Render combo field for specific_title
    return props.mapper.key === EntitiesMapperKeyEnum.ENTITIES_FIELD_SPECIFIC_TITLE
  })

  const renderInputField = computed<Boolean>(() => {
    // if the mapper field type is in textinput, numberinput, emailinput etc
    // but not if it's a combo field (specific_title)
    if (renderComboField.value) return false
    return [
      FieldTypeEnum.TEXTINPUT,
      FieldTypeEnum.NUMBERINPUT,
      FieldTypeEnum.DECIMALNUMBERINPUT,
      FieldTypeEnum.EMAILINPUT,
      FieldTypeEnum.PHONEINPUT,
      FieldTypeEnum.URLINPUT,
      FieldTypeEnum.DATEFIELD,
      FieldTypeEnum.TIMEFIELD
    ].includes(props.mapper.field_type)
  })

  const renderCheckboxField = computed<Boolean>(() => {
    return props.mapper.field_type == FieldTypeEnum.YESNO
  })

  const renderSelectField = computed<Boolean>(() => {
    return props.mapper.field_type == FieldTypeEnum.SELECT
  })

  const renderTextareaField = computed<Boolean>(() => {
    return props.mapper.field_type == FieldTypeEnum.TEXTAREA
  })

  const renderMultiSelectField = computed<Boolean>(() => {
    return props.mapper.field_type === FieldTypeEnum.MULTISELECT
  })

  const selectFieldOptions = computed(() => {
    // hardcoding the title of entity_types temporarily
    // TODO: find a solution for that later
    if (props.mapper.key === EntitiesMapperKeyEnum.ENTITIES_FIELD_ENTITY_TYPE) {
      return [
        {
          title: 'Natürliche Person',
          value: 'natural_person'
        },
        {
          title: 'Juristiche Person',
          value: 'legal_person'
        },
        {
          title: 'Institution',
          value: 'institution'
        }
      ]
    }
    return props.mapper.choices?.map((option) => {
      return {
        title: option === '' ? '---' : option,
        value: option
      }
    })
  })

  const multiSelectOptions = computed(() => {
    return (props.mapper.choices || []).map((code) => ({
      value: code,
      title: props.mapper.choice_labels?.[code] || code
    }))
  })

  const multiSelectModelValue = computed({
    get: () => {
      const val = localValue.value
      if (Array.isArray(val)) return val
      if (val === null || val === undefined || val === '') return []
      return [val]
    },
    set: (newValue: (string | number)[]) => {
      localValue.value = newValue
      emit('onValueChange', newValue)
      emit('valueChangeError', props.mapper.key, false)
      errorMessage.value = ''
    }
  })

  const multiSelectFilter = ref('')
  const filteredMultiSelectOptions = computed(() => {
    if (!multiSelectFilter.value) return multiSelectOptions.value
    return multiSelectOptions.value.filter((option) =>
      option.title.toLowerCase().includes(multiSelectFilter.value.toLowerCase())
    )
  })

  const isCopied = ref(false)
  const copyToClipboard = async () => {
    try {
      await navigator.clipboard.writeText(String(localValue.value || ''))
      isCopied.value = true

      // Track copy action for analytics when used in law-specific data or entities view
      if (props.copyTrackSource) {
        trackEvent(MetricEventType.DATA_MAPPER_VALUE_COPY, {
          source: props.copyTrackSource,
          mapper_key: props.mapper?.key,
          $insert_id: crypto.randomUUID()
        })
      }

      setTimeout(() => {
        isCopied.value = false
      }, 2000)
    } catch (err) {
      console.error('Failed to copy text: ', err)
    }
  }

  const isProcessingChange = ref(false)

  const acceptProposedChange = () => {
    if (props.proposedChange && !isProcessingChange.value) {
      isProcessingChange.value = true
      emit('acceptProposedChange', props.proposedChange)
    }
  }

  const discardProposedChange = () => {
    if (props.proposedChange && !isProcessingChange.value) {
      isProcessingChange.value = true
      emit('rejectProposedChange', props.proposedChange)
    }
  }
</script>

<template>
  <div
    v-if="mapper"
    class="flex w-full items-center"
  >
    <!-- Proposed Change Display -->
    <div
      v-if="proposedValue && proposedChange?.status === ProposedEntityChangeStatusEnum.PENDING"
      class="flex w-full items-center gap-1.5 rounded-lg border border-dashed border-purple-600 bg-purple-50
        px-2.5 py-2 text-sm"
    >
      <div class="flex-1 cursor-default truncate">
        <div class="truncate text-gray-500 line-through">{{ value }}</div>
        <div class="truncate text-gray-800">{{ proposedValue }}</div>
      </div>
      <div class="flex items-center gap-1">
        <TooltipProvider>
          <Tooltip>
            <TooltipTrigger as-child>
              <Button
                variant="secondary"
                size="icon"
                class="h-6 w-6 border border-purple-200 text-purple-600 hover:border-purple-300 hover:bg-gray-100
                  hover:text-purple-600"
                :disabled="isProcessingChange"
                @click="discardProposedChange"
              >
                <X class="h-4 w-4" />
              </Button>
            </TooltipTrigger>
            <TooltipContent
              side="top"
              variant="secondary"
              show-arrow
            >
              <p>{{ t('reject') }}</p>
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>
        <TooltipProvider>
          <Tooltip>
            <TooltipTrigger as-child>
              <Button
                variant="secondary"
                size="icon"
                class="h-6 w-6 border border-purple-200 text-purple-600 hover:border-purple-300 hover:bg-gray-100
                  hover:text-purple-600"
                :disabled="isProcessingChange"
                @click="acceptProposedChange"
              >
                <CheckIcon class="h-4 w-4" />
              </Button>
            </TooltipTrigger>
            <TooltipContent
              side="top"
              variant="secondary"
              show-arrow
            >
              <p>{{ t('accept') }}</p>
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>
      </div>
    </div>

    <!-- Regular Input Fields (shown when no proposed change) -->
    <div
      v-else
      class="w-full"
    >
      <ComboField
        v-if="renderComboField"
        v-model="modelValue"
        :name="mapper.key"
        :options="specificTitleOptions"
        :disabled="disabled"
        :error="errorMessage"
        data-cy="fieldValueInput"
      />
      <InputField
        v-else-if="renderInputField"
        v-model="modelValue"
        :type="
          mapper.field_type == FieldTypeEnum.NUMBERINPUT ||
          mapper.field_type == FieldTypeEnum.DECIMALNUMBERINPUT
            ? 'number'
            : mapper.field_type == FieldTypeEnum.TIMEFIELD
              ? 'time'
              : 'text'
        "
        :name="mapper.key"
        :rules="validationRules"
        :step="mapper.field_type == FieldTypeEnum.DECIMALNUMBERINPUT ? '0.01' : '1'"
        :disabled="disabled"
        :error="errorMessage"
        data-cy="fieldValueInput"
      />
      <SelectField
        v-else-if="renderSelectField"
        v-model="modelValue"
        :options="selectFieldOptions"
        :name="mapper.key"
        :rules="validationRules"
        :disabled="disabled"
        :error="errorMessage"
      />
      <CheckboxField
        v-else-if="renderCheckboxField"
        v-model="modelValue"
        :name="mapper.key"
        :disabled="disabled"
        :error="errorMessage"
      />
      <TextAreaField
        v-else-if="renderTextareaField"
        v-model="modelValue"
        :name="mapper.key"
        :rules="validationRules"
        :disabled="disabled"
        :error="errorMessage"
      />
      <div v-else-if="renderMultiSelectField">
        <Popover v-model:open="isMultiSelectOpen">
          <PopoverTrigger as-child>
            <Button
              variant="outline"
              :disabled="disabled"
              :class="[
                'h-auto min-h-[40px] w-full justify-between border border-gray-200 text-left font-normal'
              ]"
            >
              <div class="flex flex-wrap items-center gap-1">
                <div
                  v-for="code in multiSelectModelValue"
                  :key="code"
                  class="flex items-center rounded-full border border-gray-200 bg-white px-2 py-1 text-sm text-gray-800"
                >
                  {{ multiSelectOptions.find((o) => o.value === code)?.title || code }}
                  <button
                    type="button"
                    class="ml-1 inline-flex h-5 w-5 items-center justify-center rounded-full text-gray-800 hover:bg-gray-300"
                    @click.stop="
                      multiSelectModelValue = multiSelectModelValue.filter((v) => v !== code)
                    "
                  >
                    <X class="h-3 w-3" />
                  </button>
                </div>
                <span
                  v-if="multiSelectModelValue.length === 0"
                  class="text-sm text-gray-500"
                >
                  Optionen auswählen...
                </span>
              </div>
            </Button>
          </PopoverTrigger>
          <PopoverContent
            side="bottom"
            class="w-[--radix-popover-trigger-width] p-0"
            @open-auto-focus.prevent
          >
            <div class="border-b px-3 py-2">
              <input
                v-model="multiSelectFilter"
                type="text"
                placeholder="Suchen..."
                class="w-full border-none bg-transparent text-sm outline-none placeholder:text-gray-400 focus:ring-0"
              />
            </div>
            <div class="max-h-72 overflow-y-auto px-1 py-1">
              <div
                v-for="option in filteredMultiSelectOptions"
                :key="option.value"
                class="flex cursor-pointer items-center justify-between rounded px-3 py-2 text-sm hover:bg-gray-100"
                @click="
                  multiSelectModelValue.includes(option.value)
                    ? (multiSelectModelValue = multiSelectModelValue.filter(
                        (v) => v !== option.value
                      ))
                    : (multiSelectModelValue = [...multiSelectModelValue, option.value])
                "
              >
                {{ option.title }}
                <CheckIcon
                  v-if="multiSelectModelValue.includes(option.value)"
                  class="h-4 w-4 text-gray-600"
                />
              </div>
              <div
                v-if="filteredMultiSelectOptions.length === 0"
                class="px-3 py-2 text-sm text-gray-500"
              >
                Keine Ergebnisse
              </div>
            </div>
          </PopoverContent>
        </Popover>
      </div>
      <div v-else>
        field: {{ mapper.field_type }}
        <br />
        rules: {{ validationRules }}
        <span class="strong">NOT YET IMPLEMENTED</span>
      </div>
      <Popover
        v-if="isLongText"
        :open="isPopoverOpen"
        @update:open="isPopoverOpen = $event"
      >
        <PopoverTrigger class="inline-block w-full text-end">
          <span class="cursor-pointer text-primary underline">{{ t('showMore') }}</span>
        </PopoverTrigger>
        <PopoverContent
          side="right"
          class="max-h-[70vh] w-[30vw] max-w-[90vw] overflow-auto p-4"
        >
          <div class="mb-2 flex items-center justify-between">
            <h3 class="font-medium">{{ t('fullText') }}</h3>
            <Button
              variant="ghost"
              size="icon"
              class="h-6 w-6"
              @click="isPopoverOpen = false"
            >
              <X class="h-4 w-4" />
            </Button>
          </div>
          <p class="whitespace-pre-wrap break-words">{{ modelValue || value }}</p>
        </PopoverContent>
      </Popover>
    </div>
    <div
      v-if="!proposedChange"
      class="flex items-center"
    >
      <TooltipProvider>
        <Tooltip>
          <TooltipTrigger as-child>
            <Button
              variant="ghost"
              size="icon"
              @click="copyToClipboard"
            >
              <CopyIcon
                v-if="!isCopied"
                class="h-4 w-4"
              />
              <span
                v-else
                class="text-xs"
              >
                <CheckIcon class="h-4 w-4 text-green-500" />
              </span>
            </Button>
          </TooltipTrigger>
          <TooltipContent
            variant="secondary"
            show-arrow
          >
            <p>{{ isCopied ? t('copied') : t('copy') }}</p>
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>
    </div>
    <!--    <pre>-->
    <!--      {{ mapper }}-->
    <!--    </pre>-->
  </div>
</template>
